import { MbProFilterButton } from "@mb-pro-ui/components/table/components";
import { FilterViewProps } from "@mb-pro-ui/components/table/filters/base/types";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import {
  alpha,
  Autocomplete,
  autocompleteClasses,
  AutocompleteCloseReason,
  AutocompleteRenderInputParams,
  Box,
  ClickAwayListener,
  Divider,
  Fade,
  FilterOptionsState,
  Icon,
  IconButton,
  InputAdornment,
  InputBase,
  lighten,
  Popper,
  PopperProps,
  SxProps,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import isEmpty from "lodash/isEmpty";
import React, { ChangeEvent, HTMLAttributes, memo, MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { areEqual, FixedSizeList, ListChildComponentProps } from "react-window";
import { isEqualById } from "./utils";

export interface IdAware {
  id: string;
}

export interface AutocompleteFilterProps<OptionType> {
  options: OptionType[] | undefined;
  getOptionLabel: (option: OptionType) => string;
  getFilterValue: (option: OptionType) => string;
  renderOption?: (option: OptionType) => ReactNode;
  inputPlaceholder?: string;
  label?: string;
  getOptionDisabled?: (option: OptionType) => boolean;
  filterOptions?: (options: OptionType[], state: FilterOptionsState<OptionType>) => OptionType[];
  noOptionsText?: string;
  virtualizedRowHeight?: number;
  sortBySelection?: boolean;
  headerActions?: ReactNode;
  handleClose?: () => void;
  setDocTitle?: (optionLabels: string) => void;
  popperContainerSx?: SxProps;
}

const StyledInput = styled(InputBase)(({ theme }) => ({
  "&.MuiInputBase-root": {
    backgroundColor: theme.palette.background.paper,
    padding: 10,
    borderBottom: `1px solid ${lighten(theme.palette.divider, 0.5)}`,
    width: "100%",
    "& input": {
      borderRadius: 4,
      backgroundColor: theme.palette.common.white,
      padding: 8,
      transition: theme.transitions.create(["border-color", "box-shadow"]),
      border: `1px solid ${theme.palette.grey[400]}`,
      fontSize: 14,
      "&:focus": {
        boxShadow: `0px 0px 0px 3px ${lighten(theme.palette.primary.main, 0.5)}`,
        borderColor: `${theme.palette.primary.main}`,
      },
    },
  },
}));

export const CustomPopper = styled(Popper)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.2)}`,
  borderRadius: 6,
  zIndex: theme.zIndex.modal,
  fontSize: 13,
  color: `${theme.palette.text.primary}`,
  backgroundColor: theme.palette.background.paper,
}));

const CustomAutocompletePopper = styled("div")(({ theme }) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: "none",
    margin: 0,
    color: "inherit",
    fontSize: 13,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    backgroundColor: theme.palette.background.paper,
    padding: 0,
    [`& .${autocompleteClasses.option}`]: {
      minHeight: "auto",
      alignItems: "flex-start",
      padding: 8,
    },
  },
}));

const OptionContainer = styled("div")({
  overflow: "hidden",
  flex: "1 1 80%",
  display: "flex",
});

/**
 * rowPropsEqual makes areEqual index independent
 */
function rowPropsEqual(prevProps: ListChildComponentProps<ReactNode[]>, nextProps: ListChildComponentProps<ReactNode[]>) {
  return areEqual({ style: prevProps.style, item: prevProps.data[prevProps.index] }, { style: nextProps.style, item: nextProps.data[nextProps.index] });
}

const Row = memo((props: ListChildComponentProps<ReactNode[]>) => {
  const { data, index, style } = props;
  const dataSet = data[index];

  return (
    <div style={style}>
      {dataSet}
      <Divider />
    </div>
  );
}, rowPropsEqual);

export const PopperComponent = (props: PopperProps) => {
  const { disablePortal, anchorEl, open, ...other } = props; // eslint-disable-line @typescript-eslint/no-unused-vars
  return <CustomAutocompletePopper {...other} style={{ width: "100%" }} />;
};

export const PopperContainer = styled("div")({
  width: "350px",
});

export const VirtualizationContext = React.createContext(50);
const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

export const ListboxComponent = React.forwardRef<HTMLDivElement, HTMLAttributes<HTMLElement>>(({ children, ...other }, ref) => {
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = React.useContext(VirtualizationContext);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          itemData={itemData}
          width="100%"
          height={(itemSize + 1) * 5} // item + divider
          outerElementType={OuterElementType}
          itemSize={itemSize}
          overscanCount={3}
          itemCount={itemCount}
        >
          {Row}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const DoneBox = styled(DoneIcon, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ selected, theme }) => ({
  width: 24,
  height: 24,
  alignSelf: "center",
  visibility: selected ? "visible" : "hidden",
  color: theme.palette.success.main,
}));

export function renderAutocompleteOption<OptionType extends IdAware>(
  { style, ...props }: HTMLAttributes<HTMLLIElement>,
  option: OptionType,
  getOptionLabel: (option: OptionType) => string,
  renderOption?: (option: OptionType) => ReactNode,
  selected?: boolean
) {
  return (
    <li {...props} style={{ ...style, width: "100%" }}>
      <OptionContainer sx={{ pl: 1, pr: 1 }}>{(renderOption ?? getOptionLabel)(option)}</OptionContainer>
      {selected === undefined ? null : <DoneBox selected={selected} />}
    </li>
  );
}

export function renderAutocompleteInput(
  { InputProps: { ref }, inputProps }: AutocompleteRenderInputParams,
  placeholder: string | undefined,
  setValue: (value: string) => void
) {
  return (
    <StyledInput
      ref={ref}
      inputProps={inputProps}
      placeholder={placeholder}
      autoFocus
      sx={{
        // Copied from MUI Autocomplete
        // Avoid double tap issue on iOS
        // https://github.com/mui/material-ui/blob/2c00e3c8b808809ea569baa993bbfff8d54a0d9d/packages/mui-material/src/Autocomplete/Autocomplete.js#L94
        "@media (pointer: fine)": {
          [`&:hover .clearIcon`]: {
            visibility: "visible",
          },
        },
      }}
      endAdornment={
        inputProps.value ? (
          <InputAdornment position="end" sx={{ position: "absolute", right: (theme) => theme.spacing(1) }}>
            <IconButton onClick={() => setValue("")} size="small" className="clearIcon" sx={{ visibility: "hidden" }}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null
      }
    />
  );
}

export const AutocompleteFilterButtonLabel = ({ isEmpty, valueLabel, buttonLabel }: { isEmpty?: boolean; valueLabel?: string; buttonLabel?: string }) => (
  <>
    <Typography sx={{ visibility: "hidden", pr: 1 }}>{buttonLabel}</Typography>
    <Typography
      sx={[
        !isEmpty && {
          fontSize: "0.8rem",
          transform: "translate(0px, -18px)",
          px: "4px",
          backgroundColor: (theme) => theme.palette.common.white,
          zIndex: 1,
        },
        {
          position: "absolute",
          transition: (theme) => theme.transitions.create(["all"]),
        },
      ]}
    >
      {buttonLabel}
    </Typography>
    <Fade in={!isEmpty}>
      <Typography
        sx={{
          position: "absolute",
          transition: (theme) => theme.transitions.create(["all"]),
          overflow: "hidden",
          whiteSpace: "nowrap",
          maxWidth: "calc(100% - 60px)",
          textOverflow: "ellipsis",
          color: "text.primary",
          zIndex: 1,
        }}
      >
        {valueLabel}
      </Typography>
    </Fade>
  </>
);

const AutocompleteFilter = <OptionType extends IdAware>({
  options,
  renderOption,
  getOptionLabel,
  getFilterValue,
  label,
  filterId,
  filterValue, // used only for reseting to empty
  urlSearchParam,
  getOptionDisabled,
  filterOptions,
  inputPlaceholder,
  noOptionsText,
  virtualizedRowHeight = 50,
  sortBySelection = true,
  headerActions,
  handleClose,
  setFilterState,
  setDocTitle,
  setFilterLabel,
  popperContainerSx,
}: AutocompleteFilterProps<OptionType> & FilterViewProps) => {
  // Popper anchor element
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // the actually selected value
  const [value, setValue] = useState<OptionType>();
  // the filter input value of the <Autocomplete />
  const [inputValue, setInputValue] = useState("");

  const { formatMessage } = useIntl();

  useEffect(() => {
    if (value) {
      setDocTitle?.(getOptionLabel(value));
    }
  }, [setDocTitle, value, getOptionLabel]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopupClose = () => {
    setInputValue("");
    handleClose?.();
    setAnchorEl(null);
  };

  const handleInputChange = (_: any, value: string, reason: string) => {
    if (reason === "input") {
      console.log("setInputValue", value);
      setInputValue(value);
    }
  };

  useEffect(() => {
    if (isEmpty(filterValue)) {
      setValue(undefined);
    }
  }, [filterValue]);

  const consolidatedRef = useRef(false);

  useEffect(() => {
    if (consolidatedRef.current) {
      setFilterState(filterId, { value: value ? getFilterValue(value) : null }, value ? getFilterValue(value) : null);
    }
  }, [value, filterId, getFilterValue, setFilterState, filterValue]);

  useEffect(() => {
    if (options && options.length > 0) {
      urlSearchParam ? setValue(options.find((option) => urlSearchParam === getFilterValue(option))) : setValue(undefined);

      consolidatedRef.current = true;
    }
  }, [options, urlSearchParam, getFilterValue]);

  const open = Boolean(anchorEl);

  const sortedOptions = useMemo(() => {
    if (!options) {
      return [];
    }
    if (!sortBySelection || !value || options[0]?.id === value.id) {
      return options;
    }
    return [value].concat(options.filter((option) => option.id !== value.id));
  }, [sortBySelection, options, value]);

  const buttonLabel = label ?? formatMessage({ defaultMessage: "Select" });

  const valueLabel = value ? getOptionLabel(value) : "";

  useEffect(() => {
    setFilterLabel?.(filterId, valueLabel);
  }, [filterId, valueLabel, setFilterLabel]);

  return (
    <>
      <Box position="relative" m={1} display="inline-flex">
        <MbProFilterButton
          variant="outlined"
          endIcon={
            <Box sx={{ display: "flex", alignItems: "center", marginLeft: "40px" }}>
              <Icon fontSize="inherit">
                <DoneIcon />
              </Icon>
            </Box>
          }
          onClick={handleClick}
          active={open}
          emphasize={!isEmpty(filterValue)}
          sx={{ m: 0 }}
        >
          <AutocompleteFilterButtonLabel isEmpty={!value} valueLabel={valueLabel} buttonLabel={buttonLabel} />
        </MbProFilterButton>
        <IconButton
          id="closeButton"
          sx={{
            p: 0,
            mr: 1,
            visibility: open && value ? "visible" : "hidden",
            "*:hover+&": {
              visibility: value ? "visible" : "hidden",
            },
            "&:hover": {
              visibility: value ? "visible" : "hidden",
            },
            position: "absolute",
            right: "40px",
            top: "50%",
            transform: "translateY(-50%)",
            color: (theme) => theme.palette.success.light,
            zIndex: 9999
          }}
          onClick={(e) => {
            e.stopPropagation();
            setValue(undefined);
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <CustomPopper open={open} anchorEl={anchorEl} placement="bottom-start">
        <ClickAwayListener onClickAway={handlePopupClose}>
          <PopperContainer sx={popperContainerSx}>
            {headerActions && (
              <Box
                sx={{
                  borderBottom: (theme) => `1px solid ${lighten(theme.palette.divider, 0.5)}`,
                  padding: "8px 10px",
                  display: "flex",
                }}
              >
                {headerActions}
              </Box>
            )}
            <VirtualizationContext.Provider value={virtualizedRowHeight}>
              <Autocomplete
                value={value}
                options={sortedOptions}
                isOptionEqualToValue={isEqualById}
                getOptionDisabled={getOptionDisabled}
                open
                onChange={(event, newValue, reason) => {
                  if (event.type === "keydown" && (event as React.KeyboardEvent).key === "Backspace" && reason === "removeOption") {
                    return;
                  }
                  setValue(newValue ?? undefined);
                  handlePopupClose();
                }}
                noOptionsText={noOptionsText}
                onClose={(_event: ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
                  if (reason === "escape") {
                    handlePopupClose();
                  }
                }}
                renderInput={(params) => renderAutocompleteInput(params, inputPlaceholder, setInputValue)}
                renderOption={(props, option) => renderAutocompleteOption(props, option, getOptionLabel, renderOption)}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                getOptionLabel={getOptionLabel}
                PopperComponent={PopperComponent}
                ListboxComponent={ListboxComponent}
                filterOptions={filterOptions}
              />
            </VirtualizationContext.Provider>
          </PopperContainer>
        </ClickAwayListener>
      </CustomPopper>
    </>
  );
};

export default AutocompleteFilter;
